import api from './api';
import type { DashboardStats, MetricStat, Policy, Quote } from '@/types';

export const dashboardService = {
  async getStats(): Promise<DashboardStats> {
    try {
      // dashboard-stats provides real month-over-month growth deltas (FIX 13
      // full) sourced from MetricSnapshot history; it is allowed to fail
      // independently so the tile values still render without trend arrows.
      //
      // active_policies (item 8) is sourced from dashboard-stats too, NOT from
      // a separate `/policies/?status=active` count: `status='active'` is never
      // a stored policy status (issuance leaves status='issued'), so that count
      // always read 0. dashboard-stats computes active_policies with the same
      // date-based definition as Policy.is_active (see
      // backend/apps/reports/dashboard_metrics.py), so the tile value and its
      // delta now share one definition.
      // pending_quotes (#277) comes from dashboard-stats, NOT from a separate
      // `/quotes/?status=pending` count. That URL resolved to the DRF router
      // ROOT (`/quotes/` has no list route), which returns a route map with no
      // `count` key at all -- so `quotesRes.data.count || 0` read 0 forever.
      // dashboard-stats counts every non-terminal quote request, which is what
      // "pending" means to a user, and the tile and its delta now share one
      // definition (backend/apps/reports/dashboard_metrics.py).
      const [policiesRes, commissionRes, deltasRes] = await Promise.all([
        api.get('/policies/?page_size=1'),
        api.get('/commissions/calculations/summary/?period=all&product_type=all'),
        api
          .get('/reports/dashboard-stats/')
          .then((res) => res.data as
            | {
                metrics?: Record<string, MetricStat>;
                awaiting_policy_count?: number;
                policies_added_today?: number;
                policies_added_today_date?: string;
              }
            | undefined)
          .catch(() => undefined),
      ]);

      return {
        total_policies: policiesRes.data.count || 0,
        active_policies: Number(deltasRes?.metrics?.active_policies?.current ?? 0),
        pending_quotes: Number(deltasRes?.metrics?.pending_quotes?.current ?? 0),
        total_commission: commissionRes.data.total_commission || '0',
        policies_expiring_soon: 0,
        recent_activities: [],
        metrics: deltasRes?.metrics,
        // Quotes where the customer reported paying and is waiting on us.
        awaiting_policy_count: deltasRes?.awaiting_policy_count ?? 0,
        policies_added_today: deltasRes?.policies_added_today ?? 0,
        policies_added_today_date: deltasRes?.policies_added_today_date,
      };
    } catch {
      return {
        total_policies: 0,
        active_policies: 0,
        pending_quotes: 0,
        total_commission: '0',
        policies_expiring_soon: 0,
        recent_activities: [],
        // Same keys as the success path, so a consumer never sees the shape
        // change between the two branches.
        awaiting_policy_count: 0,
        policies_added_today: 0,
        policies_added_today_date: undefined,
      };
    }
  },

  async getRecentPolicies(limit: number = 5): Promise<Policy[]> {
    const response = await api.get(`/policies/?ordering=-created_at&page_size=${limit}`);
    return response.data.results || [];
  },

  async getRecentQuotes(limit: number = 5): Promise<Quote[]> {
    const response = await api.get(`/quotes/?ordering=-created_at&page_size=${limit}`);
    return response.data.results || [];
  },

  async getExpiringPolicies(days: number = 30, limit: number = 10): Promise<Policy[]> {
    const response = await api.get(
      `/policies/?status=active&expiry_date__lte=${getDateDaysFromNow(days)}&ordering=expiry_date&page_size=${limit}`
    );
    return response.data.results || [];
  },
};

function getDateDaysFromNow(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
}
